import { default as React, useEffect, useRef, useState } from 'react'

import { ApolloProvider } from '@apollo/client'
import {
  ThemeProvider,
  Theme,
  StyledEngineProvider,
} from '@mui/material/styles'
import { LicenseInfo } from '@mui/x-license-pro'
import * as Sentry from '@sentry/react'
import { Cable } from 'actioncable'

import 'assets/css/bootstrap.min.css'
import { Routes, AppVersionModal } from 'components/Index'
import { Analytics } from 'components/common/Analytics'
import { FullStory } from 'components/common/FullStory'
import { isRecordingAllowed } from './routes.constants'
import {
  CROSS_DOMAIN_STORAGE_URL,
  GRAPHQL_INITIAL_STATE,
  LOCAL_STORAGE_KEYS,
} from 'constants/static'
import AppContext from 'contexts/appContext'
import { GET_LOCAL_CACHED_DATA } from 'graphql/queries'
import createCustomMuiTheme from 'utils/createCustomMuiTheme'
import client from 'utils/createGraphqlClient'
import { initAnalytics } from 'utils/mixpanel'

import 'App.css'

require('dotenv').config()

client.writeQuery({
  query: GET_LOCAL_CACHED_DATA,
  data: GRAPHQL_INITIAL_STATE,
})

// MUI setup
declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

LicenseInfo.setLicenseKey(
  '7852995239c7ebf9a552f9423749d6d9Tz0xMzIzMDgsRT0xODE5MjM4Mzk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9aW5pdGlhbCxLVj0y',
)

// Or Create your Own theme:
const theme = createCustomMuiTheme()

// ENGG-9844 — the anonymous survey page must not be recorded.
//
// Session replay runs with maskAllText: false, so on /s/<token> it would ship a
// recording of a respondent typing their free-text answers — with the page
// telling them, a few pixels above, that nothing identifying is collected. The
// same goes for FullStory below. Error reporting itself is kept; it is the
// recording of the screen that cannot be allowed here.
// ENGG-8024 — the same holds for the public employee sign-up page, where people
// type their contact details.
const recordingAllowed = isRecordingAllowed(window.location.pathname)

if (!process.env.CI) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
    debug: true,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: recordingAllowed
      ? [Sentry.replayIntegration({ maskAllText: false })]
      : [],
  })
}

// ENGG-10414 — Mixpanel's token is read at module load, like Sentry, so events
// tracked before the admin is known are held; the SDK itself starts at the
// first identify of a real admin (ENGG-10416). Without a token (dev, test) it
// does nothing.
initAnalytics()

const App = () => {
  const [pageTitle, setPageTitle] = useState<
    string | { breadcrumbs: string[]; onBack: () => void } | null
  >('')
  const crossDomainIFrame = useRef<HTMLIFrameElement>({} as HTMLIFrameElement)
  const [crossDomainIFrameLoaded, setCrossDomainIFrameLoaded] = useState(false)
  const [hideAppBar, setHideAppBar] = useState(false)
  const [actionCableApp, setActionCableApp] = useState({} as Cable)

  useEffect(() => {
    if (crossDomainIFrame.current!!) {
      crossDomainIFrame.current.addEventListener(
        'load',
        onCrossDomainIframeLoad,
      )
    }
    window.addEventListener('message', handleCrossDomainMessages, false)

    return () => {
      window.removeEventListener('message', handleCrossDomainMessages)
    }
  }, [])

  const onCrossDomainIframeLoad = () => {
    if (crossDomainIFrame.current!!) {
      crossDomainIFrame!.current!.contentWindow!.postMessage(
        {
          action: 'get',
          key: LOCAL_STORAGE_KEYS.TOKEN,
        },
        CROSS_DOMAIN_STORAGE_URL || '*',
      )
    }
  }

  const saveCrossDomainToken = (value: string) => {
    if (!crossDomainIFrameLoaded) {
      return
    }

    crossDomainIFrame!.current!.contentWindow!.postMessage(
      {
        action: 'save',
        key: LOCAL_STORAGE_KEYS.TOKEN,
        value,
      },
      CROSS_DOMAIN_STORAGE_URL || '*',
    )
  }

  const handleCrossDomainMessages = (event: MessageEvent<any>) => {
    const {
      data: { action, key, value },
      origin,
    } = event

    if (action === 'returnData' && origin === CROSS_DOMAIN_STORAGE_URL) {
      switch (key) {
        case LOCAL_STORAGE_KEYS.TOKEN: {
          const tokenFromLocalStorage = localStorage.getItem(
            LOCAL_STORAGE_KEYS.TOKEN,
          )
          if (!tokenFromLocalStorage && value) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, value)
            const data = client.readQuery({ query: GET_LOCAL_CACHED_DATA })
            client.writeQuery({
              query: GET_LOCAL_CACHED_DATA,
              data: { ...data, ...{ isLoggedIn: true } },
            })
          }
        }
      }
      setCrossDomainIFrameLoaded(true)
    }
  }

  return (
    <>
      <iframe
        id="cross-domain-iframe"
        style={{ display: 'none' }}
        ref={crossDomainIFrame}
        src={CROSS_DOMAIN_STORAGE_URL}
      />
      {(!CROSS_DOMAIN_STORAGE_URL || crossDomainIFrameLoaded) && (
        <ApolloProvider client={client}>
          <AppContext.Provider
            value={{
              pageTitle,
              setPageTitle,
              hideAppBar,
              setHideAppBar,
              saveCrossDomainToken,
              actionCableApp,
              setActionCableApp,
            }}
          >
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <AppVersionModal />
                <Routes />
              </ThemeProvider>
            </StyledEngineProvider>
            {recordingAllowed && <FullStory />}
            {recordingAllowed && <Analytics />}
          </AppContext.Provider>
        </ApolloProvider>
      )}
    </>
  )
}

export default App
